import { useAccountState } from '../../redux/reducers/accountReducer';
import { HTTPStatusCode, useApi } from '../api/useApi';
import {
    CaseEvent,
    CaseEventTemplate,
    CaseMilestone,
    CaseMilestoneTemplate,
    MILESTONE_SORT_KEY_INCREMENT,
    Task,
    TaskTemplate,
    useInitialValues,
    UserAccount,
} from '../types';
import { useCaseMilestoneSignalR } from '../../signalR/useCaseMilestoneSignalR';
import { useCaseMilestoneState } from '../../redux/reducers/caseMilestoneReducer';
import { useCaseMilestoneActions } from '../../redux/actions/caseMilestoneActions';
import { useToasts } from '../../components/toasts/useToasts';

interface MilestoneUpsertResult {
    statusCode: number;
    message?: string;
    resource?: CaseMilestone;
}

export const CaseMilestoneApiRoutes = {
    getCaseMilestones: (accountId: string, caseId: string) => `account/${accountId}/case/${caseId}`,
    getCaseMilestone: (accountId: string, caseId: string, milestoneId: string) =>
        `account/${accountId}/case/${caseId}/milestone/${milestoneId}`,
    getCurrentCaseMilestone: (accountId: string, caseId: string) => `account/${accountId}/case/${caseId}/milestone/current`,
    upsertCaseMilestone: (accountId: string, caseId: string) => `account/${accountId}/case/${caseId}/milestone`,
    deleteCaseMilestone: (accountId: string, caseId: string, milestoneId: string) =>
        `account/${accountId}/case/${caseId}/milestone/${milestoneId}`,
    deleteCaseMilestones: (accountId: string, caseId: string) => `account/${accountId}/case/${caseId}/milestones`,
};

export type CaseMilestoneService = {
    milestones: CaseMilestone[];
    findMilestone: (milestoneId?: string) => CaseMilestone | undefined;
    loadMilestonesAsync: (theCaseId?: string) => Promise<void>;
    getCaseMilestoneAsync: (caseId?: string, milestoneId?: string) => Promise<CaseMilestone | undefined>;
    getCaseMilestonesAsync: (caseId?: string) => Promise<CaseMilestone[]>;
    getCurrentCaseMilestoneAsync: (caseId?: string) => Promise<CaseMilestone>;
    upsertCaseMilestoneAsync: (caseMilestone: CaseMilestone, sendToast?: boolean) => Promise<void>;
    deleteCaseMilestoneAsync: (milestoneId: string, caseId?: string, sendToast?: boolean) => Promise<void>;
    deleteCaseMilestonesAsync: (milestoneIds: string[], caseId?: string, sendToast?: boolean) => Promise<void>;
    createMilestonesForCase: (
        msTemplates: CaseMilestoneTemplate[],
        caseId: string,
        createTasks?: (tasks: Task[]) => Promise<void>,
        createEvents?: (events: CaseEvent[]) => Promise<void>,
        assignTasksToUserId?: string,
    ) => Promise<void>;
};

/**
 * This service will load milestones for a specific case and provides methods to interact with them
 * @param caseId
 * @param autoload
 * @returns
 */
export const useCaseMilestoneService = (caseId?: string): CaseMilestoneService => {
    // Hooks
    const { primaryAccount } = useAccountState();
    const { account } = (primaryAccount as UserAccount) ?? {};
    const caseMilestoneState = useCaseMilestoneState();
    const caseMilestoneActions = useCaseMilestoneActions();
    const initValues = useInitialValues();
    const toasts = useToasts();

    //API
    const api = useApi<CaseMilestone>('api/CaseMilestone');

    const sortByOrder = (a: CaseMilestone, b: CaseMilestone) => {
        return (a.sortKey ?? 0) - (b.sortKey ?? 0);
    };

    //Data Calls
    const loadMilestonesAsync = async (theCaseId?: string) => {
        caseMilestoneActions.setCaseMilestones([]);
        const id = caseId || theCaseId;
        if (!id) return;

        let milestones = await api.getAsync(CaseMilestoneApiRoutes.getCaseMilestones(account?.accountId as string, id));
        milestones = milestones.sort(sortByOrder);
        caseMilestoneActions.setCaseMilestones(milestones);
    };
    const getCaseMilestonesAsync = async (theCaseId?: string) => {
        const id = caseId || theCaseId;
        if (!id) return [];
        if (caseMilestoneState.milestones?.some((ms) => ms.caseId === id)) {
            return caseMilestoneState.milestones.filter((ms) => ms.caseId === id).sort(sortByOrder);
        } else {
            const milestones = await api.getAsync(
                CaseMilestoneApiRoutes.getCaseMilestones(account?.accountId as string, id),
            );
            // Cache so repeat lookups for this case are served from state instead of the API.
            milestones?.forEach((ms) => caseMilestoneActions.addOrReplaceCaseMilestone(ms));
            return milestones?.sort(sortByOrder);
        }
    };
    const getCaseMilestoneAsync = async (theCaseId?: string, milestoneId?: string) => {
        const id = caseId || theCaseId;
        if (!id) return undefined;

        let milestone = caseMilestoneState.milestones.find((x) => x.milestoneId == milestoneId);
        if (!milestone) {
            milestone = await api.getAsync(
                CaseMilestoneApiRoutes.getCaseMilestone(account?.accountId as string, id, milestoneId as string),
            );
            if (milestone) {
                caseMilestoneActions.addOrReplaceCaseMilestone(milestone);
            }
        }
        return milestone;
    };
    const getCurrentCaseMilestoneAsync = async (caseIdParam?: string) => {
        if (!caseMilestoneState.milestones.length) {
            const curr = caseMilestoneState.milestones.filter((x) => !x.completed).sort(sortByOrder);
            if (curr.length) return curr[0];
        }

        const theCaseId = caseId || caseIdParam;
        if (!theCaseId) return null;

        const milestone = await api.getAsync(
            CaseMilestoneApiRoutes.getCurrentCaseMilestone(account?.accountId as string, theCaseId),
        );
        return milestone;
    };
    const upsertCaseMilestoneAsync = async (caseMilestone: CaseMilestone, sendToast: boolean = false) => {
        caseMilestoneActions.addOrReplaceCaseMilestone(caseMilestone);

        const upsertResult: MilestoneUpsertResult = await api.postAsync(
            caseMilestone,
            CaseMilestoneApiRoutes.upsertCaseMilestone(account?.accountId as string, caseMilestone.caseId as string),
        );
        caseMilestoneActions.addOrReplaceCaseMilestone(upsertResult.resource as CaseMilestone);
        
        if(sendToast) {
            if(upsertResult.statusCode == HTTPStatusCode.created) {
                toasts.caseMilestones.notifyCaseMilestoneCreated(upsertResult.resource as CaseMilestone);
            } else if (upsertResult.statusCode == HTTPStatusCode.updated) {
                toasts.caseMilestones.notifyCaseMilestoneUpdated(upsertResult.resource as CaseMilestone);
            }
        }
    };
    const deleteCaseMilestoneAsync = async (milestoneId: string, theCaseId?: string, sendToast: boolean = false) => {
        const cid = caseId || theCaseId;
        if (cid) {
            const ms = caseMilestoneState.milestones.find((ms) => ms.milestoneId === milestoneId);
            caseMilestoneActions.removeCaseMilestone(milestoneId);
            await api.deleteAsync(
                [milestoneId],
                CaseMilestoneApiRoutes.deleteCaseMilestone(account?.accountId as string, cid, milestoneId),
            );
            if(sendToast) {
                toasts.caseMilestones.notifyCaseMilestoneDeleted(ms as CaseMilestone);
            }
        }
    };
    const deleteCaseMilestonesAsync = async (milestoneIds: string[], theCaseId?: string, sendToast: boolean = false) => {
        const cid = caseId || theCaseId;
        if (cid) {
            caseMilestoneActions.removeCaseMilestones(milestoneIds);
            await api.deleteAsync(
                milestoneIds,
                CaseMilestoneApiRoutes.deleteCaseMilestones(account?.accountId as string, cid),
            );
            if(sendToast) {
                toasts.caseMilestones.notifyCaseMilestonesDeleted(milestoneIds.length);
            }
        }
    };

    //Logic
    const findMilestone = (milestoneId?: string) => {
        return (caseMilestoneState.milestones || []).find((x) => x.milestoneId == milestoneId);
    };
    const createMilestonesForCase = async (
        msTemplates: CaseMilestoneTemplate[],
        caseId: string,
        createTasks?: (tasks: Task[]) => Promise<void>,
        createEvents?: (events: CaseEvent[]) => Promise<void>,
        assignTasksToUserId?: string,
    ) => {
        msTemplates.forEach(async (ms: CaseMilestoneTemplate, index: number) => {
            const today = new Date();

            //set props
            const actMilestone: CaseMilestone = initValues.caseMilestone(caseId);
            actMilestone.name = ms.name;
            actMilestone.description = ms.description;
            actMilestone.deliverables = ms.deliverables;
            actMilestone.sortKey = (index+1)*MILESTONE_SORT_KEY_INCREMENT;

            //set due date
            const daysFromNow = new Date();
            if (!!ms.dueDaysFromCreated) {
                daysFromNow.setDate(today.getDate() + (ms.dueDaysFromCreated || 0));
                actMilestone.dueDate = daysFromNow;
            }

            //save Milestone
            await upsertCaseMilestoneAsync(actMilestone);

            //create tasks
            let tasks = [] as Task[] | undefined;
            const assignToUser = assignTasksToUserId || primaryAccount?.user.userId;
            tasks = ms.tasks?.map((task: TaskTemplate) => {
                return {
                    ...initValues.task(assignToUser as string, caseId),
                    title: task.title || task.text?.substring(0, 50) || 'New Task',
                    text: task.text,
                    billable: task.billable,
                    milestoneId: actMilestone.milestoneId,
                } as Task;
            });
            !!tasks?.length && !!createTasks && (await createTasks(tasks));

            //create events
            let events = [] as CaseEvent[] | undefined;
            events = ms.events?.map((evt: CaseEventTemplate) => {
                const eventDate = new Date();
                eventDate.setDate(eventDate.getDate() + (evt.daysFromRetained || 0));

                return {
                    ...initValues.caseEvent(caseId),
                    event: evt.eventName,
                    description: evt.description,
                    date: eventDate,
                    due: evt.due,
                    milestoneId: actMilestone.milestoneId,
                } as CaseEvent;
            });
            !!events?.length && !!createEvents && (await createEvents(events));
        });
    };

    return {
        milestones: [...(caseMilestoneState.milestones || [])],
        findMilestone,
        loadMilestonesAsync,
        getCaseMilestoneAsync,
        getCaseMilestonesAsync,
        getCurrentCaseMilestoneAsync,
        upsertCaseMilestoneAsync,
        deleteCaseMilestoneAsync,
        deleteCaseMilestonesAsync,
        createMilestonesForCase,
    };
};
